import type { HelpKey } from "../i18n/index";

export interface KeyHandlers {
  togglePause(): void;
  stepSpeed(dir: 1 | -1): void;
  toggleScale(): void;
  toggleLabels(): void;
  toggleOrbits(): void;
  home(): void;
  /** Close the menu, else collapse the info card, else go home. */
  escape(): void;
  toggleStats(): void;
  toggleMenu(): void;
  openSearch(): void;
  openHelp(): void;
}

export interface KeyBinding {
  /** How the key is written in the help list; words are spelled out per language. */
  display: string;
  /** `KeyboardEvent.key` values that trigger the binding. */
  keys: readonly string[];
  /** Its description in the Help tab. */
  label: HelpKey;
  run(h: KeyHandlers): void;
}

/** Every shortcut in help order; `bindKeys` and the Help tab both read this table. */
export const KEY_HELP: readonly KeyBinding[] = [
  { display: "Space", keys: [" "], label: "pause", run: (h) => h.togglePause() },
  {
    display: "→ ↑",
    keys: ["ArrowRight", "ArrowUp"],
    label: "faster",
    run: (h) => h.stepSpeed(1),
  },
  {
    display: "← ↓",
    keys: ["ArrowLeft", "ArrowDown"],
    label: "slower",
    run: (h) => h.stepSpeed(-1),
  },
  { display: "T", keys: ["t", "T"], label: "scale", run: (h) => h.toggleScale() },
  { display: "L", keys: ["l", "L"], label: "labels", run: (h) => h.toggleLabels() },
  { display: "O", keys: ["o", "O"], label: "orbits", run: (h) => h.toggleOrbits() },
  { display: "H", keys: ["h", "H"], label: "home", run: (h) => h.home() },
  {
    display: "Esc",
    keys: ["Escape"],
    label: "escape",
    run: (h) => h.escape(),
  },
  { display: "M", keys: ["m", "M"], label: "menu", run: (h) => h.toggleMenu() },
  { display: "/", keys: ["/"], label: "search", run: (h) => h.openSearch() },
  { display: "?", keys: ["?"], label: "help", run: (h) => h.openHelp() },
  { display: "F", keys: ["f", "F"], label: "stats", run: (h) => h.toggleStats() },
];

const BY_KEY = new Map<string, KeyBinding>(
  KEY_HELP.flatMap((b) => b.keys.map((k): [string, KeyBinding] => [k, b])),
);

export interface KeyEventLike {
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  target: unknown;
  /** Set when an earlier handler (e.g. the menu) already acted on the event. */
  defaultPrevented?: boolean;
}

const FORM_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT", "BUTTON"]);

/** True for browser shortcuts (Ctrl, Cmd, Alt) and keys typed into form controls or editable content. */
export function shouldIgnoreKey(e: KeyEventLike): boolean {
  if (e.ctrlKey || e.metaKey || e.altKey) return true;
  const t = e.target as { tagName?: unknown; isContentEditable?: unknown } | null;
  if (typeof t !== "object" || t === null) return false;
  if (t.isContentEditable === true) return true;
  return typeof t.tagName === "string" && FORM_TAGS.has(t.tagName.toUpperCase());
}

export function bindKeys(target: Window, h: KeyHandlers): () => void {
  const onKey = (e: KeyboardEvent): void => {
    if (e.defaultPrevented || shouldIgnoreKey(e)) return;
    const binding = BY_KEY.get(e.key);
    if (!binding) return;
    binding.run(h);
    e.preventDefault();
  };
  target.addEventListener("keydown", onKey);
  return () => target.removeEventListener("keydown", onKey);
}
